import { useMemo, useState } from 'react';
import { format } from 'date-fns';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button as TamaguiButton, XStack, YStack } from 'tamagui';

import {
  AppCard,
  AppCardBody,
  AppDisclosureGlyph,
  AppEmptyState,
  AppGuideCardButton,
  AppSectionHeader,
  AppText,
} from '../../../ui-foundation';
import {
  buildGuideDetailPath,
  capitalise,
  fallbackHelperPackIcon,
  helperPackFooterLabel,
  splitGuideItems,
  type GuideItem,
} from './guideData';
import { GUIDE_HUB_META, type GuideSurfaceSection } from '../../../ui-foundation/features/guides';

export type { GuideItem, Playbook, PlaybookStep, ContentCard } from './guideData';

type PlaybookListProps = {
  section: GuideSurfaceSection;
  guides: GuideItem[];
  loading?: boolean;
  emptyTitle?: string;
  emptyBody?: string;
  showSectionHeaders?: boolean;
  helperPacksTitle?: string;
  helperPacksSubtitle?: string;
  supportCardsTitle?: string;
  supportCardsSubtitle?: string;
};

export default function PlaybookList({
  section,
  guides,
  loading = false,
  emptyTitle = 'No support is live yet',
  emptyBody = 'Active support guides and cards will show here when they are live for this section.',
  showSectionHeaders = false,
  helperPacksTitle = GUIDE_HUB_META.helperPacksTitle,
  helperPacksSubtitle,
  supportCardsTitle = GUIDE_HUB_META.supportCardsTitle,
  supportCardsSubtitle,
}: PlaybookListProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedCardId, setExpandedCardId] = useState<string | null>(null);
  const { helperPacks, supportCards } = useMemo(() => splitGuideItems(guides), [guides]);

  if (loading) {
    return (
      <AppText color="$colorMuted" textAlign="center" py="$lg">
        Loading support hub...
      </AppText>
    );
  }

  if (guides.length === 0) {
    return <AppEmptyState title={emptyTitle} body={emptyBody} />;
  }

  const from = `${location.pathname}${location.search}`;

  return (
    <div className="space-y-4">
      {helperPacks.length ? (
        <div className="space-y-3">
          {showSectionHeaders ? (
            <AppSectionHeader title={helperPacksTitle} subtitle={helperPacksSubtitle} />
          ) : supportCards.length ? (
            <AppText color="$colorMuted" fontSize="$xs" fontWeight="700" lineHeight="$sm" textTransform="uppercase" letterSpacing={1.1}>
              Helper packs
            </AppText>
          ) : null}
          <div className="overflow-x-auto pb-1">
            <div className="flex min-w-max gap-3 pr-1">
              {helperPacks.map((guide) => (
                <AppGuideCardButton
                  type="button"
                  key={`${guide.kind}-${guide.id}`}
                  minHeight={104}
                  className="w-[216px] shrink-0"
                  onPress={() =>
                    navigate(buildGuideDetailPath(section, guide), {
                      state: { from },
                    })
                  }
                >
                  <YStack flex={1} justifyContent="space-between" gap="$sm">
                    <YStack gap="$sm">
                      <XStack alignItems="flex-start" gap="$sm">
                        <AppText color="$primaryForeground" fontSize={26} lineHeight={28}>
                          {guide.icon || fallbackHelperPackIcon(guide.title)}
                        </AppText>
                        <AppText
                          flex={1}
                          color="$primaryForeground"
                          fontSize="$sm"
                          lineHeight="$sm"
                          fontWeight="700"
                        >
                          {guide.title}
                        </AppText>
                      </XStack>
                      <AppText color="$primaryForeground" opacity={0.82} fontSize="$sm" lineHeight={18}>
                        {guide.description}
                      </AppText>
                    </YStack>
                    <AppText color="$primaryForeground" opacity={0.68} fontSize="$xs" lineHeight={16} fontWeight="600">
                      {guide.difficulty
                        ? `${capitalise(guide.difficulty)} • ${helperPackFooterLabel(guide)}`
                        : helperPackFooterLabel(guide)}
                    </AppText>
                  </YStack>
                </AppGuideCardButton>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      {supportCards.length ? (
        <div className="space-y-3">
          {showSectionHeaders ? (
            <AppSectionHeader title={supportCardsTitle} subtitle={supportCardsSubtitle} />
          ) : (
            <AppText color="$colorMuted" fontSize="$xs" fontWeight="700" lineHeight="$sm" textTransform="uppercase" letterSpacing={1.1}>
              Support cards
            </AppText>
          )}
          <div className="space-y-3">
            {supportCards.map((guide) => {
              const isExpanded = expandedCardId === guide.id;

              return (
                <AppCard key={`${guide.kind}-${guide.id}`}>
                  <TamaguiButton
                    unstyled
                    type="button"
                    width="100%"
                    onPress={() => setExpandedCardId(isExpanded ? null : guide.id)}
                    pressStyle={{ opacity: 0.94 }}
                  >
                    <AppCardBody>
                      <XStack alignItems="center" justifyContent="space-between" gap="$sm">
                        <AppText flex={1} paddingRight="$sm" fontSize="$sm" lineHeight="$sm" fontWeight="700">
                          {guide.title}
                        </AppText>
                        <AppDisclosureGlyph open={isExpanded} />
                      </XStack>
                    </AppCardBody>
                  </TamaguiButton>

                  {isExpanded ? (
                    <AppCardBody pt={0} gap="$xs">
                      {guide.summary ? (
                        <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md" whiteSpace="pre-wrap">
                          {guide.summary}
                        </AppText>
                      ) : null}
                      <AppText fontSize="$sm" lineHeight="$md" whiteSpace="pre-wrap">
                        {guide.body}
                      </AppText>
                      {guide.detail ? (
                        <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md" whiteSpace="pre-wrap">
                          {guide.detail}
                        </AppText>
                      ) : null}
                      {guide.link_url ? (
                        <a
                          href={guide.link_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm font-semibold text-primary underline-offset-4 hover:underline"
                        >
                          {guide.link_label || 'Find out more'}
                        </a>
                      ) : null}
                      {guide.last_reviewed_at ? (
                        <AppText color="$colorMuted" fontSize="$xs" lineHeight="$sm">
                          Last reviewed: {format(new Date(guide.last_reviewed_at), 'd MMMM yyyy')}
                        </AppText>
                      ) : null}
                    </AppCardBody>
                  ) : null}
                </AppCard>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}
